/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import '@adobe-commerce/elsie/components/Field/Field.css';
import { classes } from '@adobe-commerce/elsie/lib';
import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';

export interface FieldProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  success?: string;
  disabled?: boolean;
  children?: VNode;
  size?: 'medium' | 'large';
}

export const Field: FunctionComponent<FieldProps> = ({
  className,
  label,
  error,
  hint,
  success,
  size = 'medium',
  disabled = false,
  children,
  ...props
}) => {
  const id =
    children?.props?.id ?? `dropin-field-${Math.random().toString(36)}`;
  const ChildComponent =
    children && typeof children.type !== 'string' ? children.type : null;

  return (
    <div {...props} className={classes(['dropin-field', className])}>
      {label && (
        <label
          className={classes([
            'dropin-field__label',
            ['dropin-field__label--disabled', disabled],
            `dropin-field__label--${size}`,
          ])}
          htmlFor={id}
        >
          {label}
        </label>
      )}

      <div className={classes(['dropin-field__content'])}>
        {ChildComponent && children && (
          <ChildComponent
            {...children.props}
            id={id}
            key={children.key}
            disabled={disabled}
            size={size}
            error={!!error}
            success={!!success && !error}
          />
        )}
      </div>

      <div
        className={classes([
          'dropin-field__hint',
          [`dropin-field__hint--${size}`, size],
          ['dropin-field__hint--error', !!error],
          ['dropin-field__hint--success', !!success && !error],
          ['dropin-field__hint--disabled', !!disabled],
        ])}
      >
        {error || success || hint}
      </div>
    </div>
  );
};
